import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import SortedTable from '../../../components/sorted-table/SortedTable';
import {
    formatMoneyKr,
    formatOrganizationNumber,
    formatToNbDate,
    getDateFormatted,
    getFirstDateOfYear,
} from '../../../utils';
import {
    DATE_FROM,
    DATE_TO,
    LIMIT,
    OFFSET,
    SORT_BY,
    SORT_DIRECTION,
    TEMPLATE_ID,
} from '../../../components/sorted-table/sorted-table-model';
import { getTableData } from '../../organization-actions';
import Loading from '../../../components/Loading';
import { DownloadButtons } from '../../../components/sorted-table/DownloadButtons';
import moment from 'moment/moment';
import { ContextErrorMessage } from '@sb1/ffe-context-message-react';
import { getTemplateCausesForOrganization } from '../../../template/template-actions';
import { getTemplateCausesForOrganizationId } from '../../../template/template-reducer';
import { Filters } from '../../../components/filters-form/Filters';
import { DatePickerRange } from '../../../components/filters-form/date-picker-range/DatePickerRange';
import { getFormActions } from '../../../utils/form-helpers';
import filtersFormConfig from '../../../components/filters-form/filters-form-config';
import { OrganizationCauseDropdown } from '../../../components/filters-form/organization-cause-dropdown/OrganizationCauseDropdown';
import { GiverDataTypeRadios } from '../../../components/filters-form/giver-data-type-radios/GiverDataTypeRadios';
import { getGiverdataTableQueryString, getGiverdataTableUrl } from '../../organization-model';
import { formatPhoneNumber } from '../../../utils/phone-utils';
import styles from './organization-giverdata.module.less';
import TrackEventLink from '../../../components/track-event-link/TrackEventLink';

const TABLE_DEFINITIONS = {
    sharedDisplayMapper: ({ property, data }) => {
        switch (property) {
            case 'projectId':
                return (
                    <TrackEventLink
                        category={'giverdata'}
                        action={'go-to-project'}
                        target={'_blank'}
                        to={`/project/${data}`}
                    >
                        {data}
                    </TrackEventLink>
                );
            case 'amount':
                return formatMoneyKr(data);
            case 'phoneNumber':
                return formatPhoneNumber(data);
            case 'organizationNumber':
                return formatOrganizationNumber(data);
            case 'publishedAt':
                return moment(data).format('DD.MM.YYYY');
            default:
                return data;
        }
    },
    giverData: {
        dataMapper: (d) => ({
            transactionId: d.transactionId,
            projectId: d.projectId,
            date: d.date,
            amount: d.amount,
            email: d.email,
            phoneNumber: d.phoneNumber,
        }),
        columns: [
            { header: 'Transaksjon', property: 'transactionId' },
            { header: 'Spleis', property: 'projectId' },
            { header: 'Beløp', property: 'amount' },
            { header: 'Epost', property: 'email', canSort: false },
            { header: 'Telefonnummer', property: 'phoneNumber', canSort: false },
            { header: 'Dato', property: 'date' },
        ],
        displayMapper: ({ property, data }) => {
            switch (property) {
                default:
                    return TABLE_DEFINITIONS.sharedDisplayMapper({ property, data });
            }
        },
        downloadFileName: 'giverdata',
    },
    taxRefund: {
        dataMapper: (d) => ({
            transactionId: d.transactionId,
            projectId: d.projectId,
            date: d.date,
            amount: d.amount,
            name: d.organizationName || [d.firstName, d.lastName].filter(Boolean).join(', '),
            address: [d.address, d.postalCode, d.postalName].filter(Boolean).join(', '),
            nationalIdNumber: d.nationalIdNumber,
            organizationNumber: d.organizationNumber,
        }),
        columns: [
            { header: 'Transaksjon', property: 'transactionId' },
            { header: 'Spleis', property: 'projectId' },
            { header: 'Beløp', property: 'amount' },
            { header: 'Navn', property: 'name', canSort: false },
            { header: 'Adresse', property: 'address', canSort: false },
            { header: 'Fødselsnummer', property: 'nationalIdNumber', canSort: false },
            { header: 'Organisasjonsnr', property: 'organizationNumber', canSort: false },
            { header: 'Dato', property: 'date' },
        ],
        displayMapper: ({ property, data }) => {
            switch (property) {
                default:
                    return TABLE_DEFINITIONS.sharedDisplayMapper({ property, data });
            }
        },
        downloadFileName: 'skattefradrag',
    },
    all: {
        dataMapper: (d) => ({
            transactionId: d.transactionId,
            projectId: d.projectId,
            date: d.date,
            amount: d.amount,
            commentName: d.commentName,
            comment: d.comment,
            campaignId: d.campaignId,
        }),
        columns: [
            { header: 'Transaksjon', property: 'transactionId' },
            { header: 'Spleis', property: 'projectId' },
            { header: 'Beløp', property: 'amount' },
            { header: 'Hilsen', property: 'comment', canSort: false },
            { header: 'Navn på hilsen', property: 'commentName', canSort: false },
            { header: 'Dato', property: 'date' },
        ],
        displayMapper: ({ property, data }) => {
            switch (property) {
                default:
                    return TABLE_DEFINITIONS.sharedDisplayMapper({ property, data });
            }
        },
        downloadFileName: 'alle-transaksjoner',
    },
    collectors: {
        dataMapper: (d) => ({
            projectId: d.projectId,
            name: d.name || (d.firstName || '') + ' ' + (d.lastName || ''),
            email: d.email,
            phoneNumber: d.phoneNumber,
            publishedAt: d.publishedAt,
            templateCauseTitle: d.templateCauseTitle,
        }),
        columns: [
            { header: 'Spleis', property: 'projectId' },
            { header: 'Navn', property: 'name', canSort: false },
            { header: 'Epost', property: 'email', canSort: false },
            { header: 'Telefonnummer', property: 'phoneNumber', canSort: false },
            { header: 'Opprettet', property: 'publishedAt' },
            { header: 'Aksjon', property: 'templateCauseTitle' },
        ],
        displayMapper: ({ property, data }) => {
            switch (property) {
                default:
                    return TABLE_DEFINITIONS.sharedDisplayMapper({ property, data });
            }
        },
        downloadFileName: 'innsamlere',
    },
};

const { setFormFields } = getFormActions(filtersFormConfig);

export const GiverdataTable = ({ organizationId }) => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const causes = useSelector((state) => getTemplateCausesForOrganizationId(state, organizationId));

    const [tableData, setTableData] = useState([]);
    const [totalCount, setTotalCount] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const initialState = {
        dataType: 'giverData',
        dateFrom: getFirstDateOfYear(new Date()),
        dateTo: getDateFormatted(new Date()),
        templateId: null,
    };

    const limit = searchParams.get(LIMIT) || 5;
    const offset = searchParams.get(OFFSET) || 0;
    const sortDirection = searchParams.get(SORT_DIRECTION) || 'DESC';
    const sortBy = searchParams.get(SORT_BY);

    const dataType = searchParams.get('dataType') || initialState.dataType;
    const dateFrom = searchParams.get(DATE_FROM) || initialState.dateFrom;
    const dateTo = searchParams.get(DATE_TO) || initialState.dateTo;
    const templateId = searchParams.get(TEMPLATE_ID) || initialState.templateId;

    useEffect(() => {
        dispatch(
            setFormFields({ dateFrom: formatToNbDate(dateFrom), dateTo: formatToNbDate(dateTo), templateId, dataType })
        );
    }, [dispatch, dateFrom, dateTo, templateId, dataType]);

    useEffect(() => {
        dispatch(getTemplateCausesForOrganization({ organizationId }));
    }, [dispatch, organizationId]);

    useEffect(() => {
        if (organizationId) {
            const url = getGiverdataTableUrl({ type: dataType, organizationId });
            const queryString = getGiverdataTableQueryString({
                limit,
                offset,
                sortBy,
                sortDirection,
                dateFrom,
                dateTo,
                dataType,
                templateId,
            });
            setLoading(true);
            dispatch(
                getTableData({
                    url,
                    queryString,
                })
            )
                .then((data) => {
                    setTableData(data.body.rows);
                    setTotalCount(data.body.totalCount);
                    setError(null);
                })
                .catch((error) => {
                    setError(error?.body?.errors);
                })
                .finally(() => setLoading(false));
        }
    }, [organizationId, dispatch, limit, offset, dataType, dateFrom, dateTo, sortDirection, sortBy, templateId]);

    return (
        <div className={styles.giverTable}>
            <Filters initialState={initialState} queryParamsToPersist={[]} category={'giverdata-filters'}>
                <GiverDataTypeRadios />
                <OrganizationCauseDropdown list={causes?.filter((c) => !!c.title)} />
                <DatePickerRange category={'giverdata-filters'} />
            </Filters>
            {error && (
                <ContextErrorMessage compact={true} className={styles.errorMessage}>
                    {error?.global || 'Noe gikk feil!'}
                </ContextErrorMessage>
            )}
            {loading ? (
                <Loading />
            ) : (
                <>
                    <SortedTable
                        data={tableData.map(TABLE_DEFINITIONS[dataType].dataMapper)}
                        columns={TABLE_DEFINITIONS[dataType].columns}
                        mapper={TABLE_DEFINITIONS[dataType].displayMapper}
                        hasPagination={true}
                        numberOfRows={totalCount}
                        rowsPerPage={limit}
                        hasQueryStringSort={true}
                        defaultSortDirection={sortDirection}
                        defaultSortBy={sortBy}
                    />
                    {tableData?.length > 0 && (
                        <DownloadButtons
                            downloadUrl={`${getGiverdataTableUrl({ type: dataType, organizationId })}.csv?${getGiverdataTableQueryString(
                                {
                                    sortBy,
                                    sortDirection,
                                    dateFrom,
                                    dateTo,
                                    templateId,
                                    dataType,
                                }
                            )}`}
                            downloadUrlExcel={`${getGiverdataTableUrl({ type: dataType, organizationId })}.csv?${getGiverdataTableQueryString(
                                {
                                    sortBy,
                                    sortDirection,
                                    dateFrom,
                                    dateTo,
                                    templateId,
                                    dataType,
                                }
                            )}&excel=${true}`}
                            downloadedFileExtension={'csv'}
                            downloadedFileExtensionExcel={'csv'}
                            fileName={TABLE_DEFINITIONS[dataType].downloadFileName}
                            trackingCategory={`giverdata-${dataType}`}
                        />
                    )}
                </>
            )}
        </div>
    );
};
